import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KEY = "gcv_ref_code";

export function ReferralAutoClaim() {
  const { user } = useAuth();
  useEffect(() => {
    const code = localStorage.getItem(KEY);
    if (!user || !code) return;
    api.post("/loyalty/referral/claim", { code, consent: true })
      .then((r) => {
        toast.success(`Welcome to ${r.data.program.name}!${r.data.bonus ? ` +${r.data.bonus} bonus points!` : ""}`);
        localStorage.removeItem(KEY);
      })
      .catch(() => localStorage.removeItem(KEY));
  }, [user]);
  return null;
}

export default function JoinRewards() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const code = (params.get("ref") || "").toUpperCase();
  const [info, setInfo] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) { setInfo(null); return; }
    localStorage.setItem(KEY, code);
    api.get(`/loyalty/referral/info?code=${code}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [code]);

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/loyalty/referral/claim", { code, consent: true });
      localStorage.removeItem(KEY);
      toast.success(`Welcome to ${data.program.name}!${data.bonus ? ` +${data.bonus} bonus points!` : ""}`);
      nav("/my-rewards");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (info === undefined) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;
  if (!info) return (
    <div className="py-40 text-center text-zinc-400" data-testid="join-rewards-invalid">
      This referral link isn't valid anymore. <Link to="/marketplace" className="text-rose-400">Browse the marketplace</Link>
    </div>
  );

  const p = info.program;
  const totalBonus = (info.referred_bonus_points || 0) + (info.welcome_bonus || 0);
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="join-rewards-page">
      <div className="max-w-md w-full bg-gradient-to-b from-rose-500/10 to-transparent border border-rose-500/30 rounded-3xl p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-rose-500/20 flex items-center justify-center mx-auto mb-4"><Gift className="text-rose-400" size={26} /></div>
        <p className="overline text-rose-400">Gulf Coast VIP Rewards</p>
        <h1 className="font-display font-extrabold text-2xl mt-1">{info.referrer_name} invited you to join</h1>
        <p className="font-display font-bold text-xl text-rose-400 mt-2">{p.name}</p>
        {p.description && <p className="text-sm text-zinc-400 mt-2">{p.description}</p>}
        {totalBonus > 0 && (
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2">
            <Star size={14} /> Join now and get {totalBonus} {p.points_name || "points"}
          </p>
        )}
        {user ? (
          <button onClick={claim} disabled={busy} data-testid="join-rewards-claim"
            className="w-full mt-6 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold py-3.5 rounded-full">
            {busy ? "Joining..." : "Join & claim bonus"}
          </button>
        ) : (
          <div className="mt-6 space-y-2">
            <Link to="/login" className="block w-full bg-rose-500 hover:bg-rose-600 font-bold py-3.5 rounded-full" data-testid="join-rewards-login">Log in or sign up to join</Link>
            <p className="text-[11px] text-zinc-500">Your invite is saved — you'll join automatically after logging in.</p>
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-4">By joining you accept the program terms and consent to loyalty tracking for this program.</p>
      </div>
    </div>
  );
}
